import {
  Building2,
  BriefcaseMedical,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  FileCheck2,
  FileText,
  RefreshCw,
  ShieldCheck,
  Users,
  WalletCards,
} from "lucide-react";

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import { API_BASE_URL } from "../config/apiConfig";

import "../styles/FacilityDashboard.css";


function FacilityDashboard() {
  const navigate =
    useNavigate();

  const [
    facility,
    setFacility,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const getToken = () => {
    return (
      localStorage.getItem(
        "zethAuthToken"
      ) ||
      sessionStorage.getItem(
        "zethAuthToken"
      )
    );
  };

  const loadFacility =
    useCallback(
      async () => {
        try {
          setLoading(true);
          setError("");

          const token =
            getToken();

          if (!token) {
            navigate(
              "/facility/login",
              {
                replace: true,
              }
            );

            return;
          }

          const response =
            await fetch(
              `${API_BASE_URL}/facilities/profile`,
              {
                method: "GET",

                headers: {
                  Authorization:
                    `Bearer ${token}`,

                  "Content-Type":
                    "application/json",
                },
              }
            );

          let data = {};

          try {
            data =
              await response.json();
          } catch {
            data = {};
          }

          if (
            response.status ===
              401 ||
            response.status ===
              403
          ) {
            navigate(
              "/facility/login",
              {
                replace: true,
              }
            );

            return;
          }

          if (!response.ok) {
            throw new Error(
              data?.message ||
                "Unable to load facility profile."
            );
          }

          setFacility(
            data?.facility ||
              data
          );
        } catch (
          loadError
        ) {
          console.error(
            "Facility dashboard error:",
            loadError
          );

          setError(
            loadError?.message ||
              "Unable to load facility dashboard."
          );
        } finally {
          setLoading(false);
        }
      },
      [navigate]
    );

  useEffect(() => {
    loadFacility();
  }, [loadFacility]);

  const formatStatus = (
    value
  ) => {
    if (!value) {
      return "Not Set";
    }

    return String(value)
      .replace(/_/g, " ")
      .replace(
        /\b\w/g,
        (character) =>
          character.toUpperCase()
      );
  };

  const operationalStatus =
    facility?.status ||
    "pending";

  const approvalStatus =
    facility?.approval_status ||
    "pending";

  const isOperational =
    facility?.is_active ===
    true;

  const profileFields = [
    facility?.facility_name,
    facility?.facility_type,
    facility?.contact_person,
    facility?.email,
    facility?.phone,
    facility?.address,
    facility?.city,
    facility?.province,
    facility?.postal_code,
    facility?.country,
  ];

  const completedFields =
    profileFields.filter(
      (value) =>
        value !== null &&
        value !== undefined &&
        String(value).trim() !==
          ""
    ).length;

  const profileCompletion =
    profileFields.length
      ? Math.round(
          (completedFields /
            profileFields.length) *
            100
        )
      : 0;

  const modules = [
    {
      title:
        "Staffing Requests",

      description:
        "Create and manage requests for healthcare professionals.",

      icon:
        ClipboardList,

      status:
        "Coming Next",
    },

    {
      title:
        "Assignments",

      description:
        "Review active, upcoming and completed staffing assignments.",

      icon:
        BriefcaseMedical,

      status:
        "Coming Next",
    },

    {
      title:
        "Professionals",

      description:
        "View healthcare professionals assigned to your facility.",

      icon:
        Users,

      status:
        "Coming Next",
    },

    {
      title:
        "Contacts",

      description:
        "Manage scheduling, billing and compliance contacts.",

      icon:
        Users,

      status:
        "Coming Next",
    },

    {
      title:
        "Contracts",

      description:
        "Review facility contracts, terms and service agreements.",

      icon:
        FileText,

      status:
        "Coming Next",
    },

    {
      title:
        "Documents",

      description:
        "Upload and manage facility compliance documents.",

      icon:
        FileCheck2,

      status:
        "Coming Next",
    },

    {
      title:
        "Billing",

      description:
        "Manage billing details, payment terms and invoice information.",

      icon:
        WalletCards,

      status:
        "Coming Next",
    },

    {
      title:
        "Facility Profile",

      description:
        "Review your organization's facility information.",

      icon:
        Building2,

      status:
        "Available",

      action: () =>
        window.scrollTo({
          top: 0,
          behavior:
            "smooth",
        }),
    },
  ];

  if (loading) {
    return (
      <main className="facility-dashboard-page">
        <div className="facility-dashboard-loading">
          Loading facility
          dashboard...
        </div>
      </main>
    );
  }

  return (
    <main className="facility-dashboard-page">
      <header className="facility-dashboard-header">
        <div>
          <p className="facility-dashboard-eyebrow">
            HEALTHCARE FACILITY
            PORTAL
          </p>

          <h1>
            Healthcare Facility
            Dashboard
          </h1>

          <p className="facility-dashboard-subtitle">
            Manage your staffing,
            assignments, facility
            information,
            compliance and billing
            from one secure
            workspace.
          </p>
        </div>

        <div className="facility-dashboard-header-actions">
          <button
            type="button"
            className="facility-dashboard-button secondary"
            onClick={
              loadFacility
            }
            disabled={
              loading
            }
          >
            <RefreshCw
              size={17}
            />

            Refresh
          </button>
        </div>
      </header>

      {error && (
        <div className="facility-dashboard-error">
          {error}
        </div>
      )}

      <section className="facility-dashboard-identity">
        <div className="facility-dashboard-identity-icon">
          <Building2
            size={29}
          />
        </div>

        <div className="facility-dashboard-identity-copy">
          <p>
            Facility Account
          </p>

          <strong>
            {facility
              ?.facility_name ||
              "Healthcare Facility"}
          </strong>

          <span>
            {[
              facility?.city,
              facility?.province,
            ]
              .filter(Boolean)
              .join(", ") ||
              "Location not provided"}
          </span>
        </div>

        <div className="facility-dashboard-identity-status">
          <span
            className={`facility-dashboard-status ${
              isOperational
                ? "active"
                : "inactive"
            }`}
          >
            {formatStatus(
              operationalStatus
            )}
          </span>
        </div>
      </section>

      <section className="facility-dashboard-stat-grid">
        <article>
          <div className="facility-dashboard-stat-icon">
            <CheckCircle2
              size={23}
            />
          </div>

          <div>
            <strong>
              {formatStatus(
                approvalStatus
              )}
            </strong>

            <span>
              Approval Status
            </span>
          </div>
        </article>

        <article>
          <div className="facility-dashboard-stat-icon">
            <ShieldCheck
              size={23}
            />
          </div>

          <div>
            <strong>
              {formatStatus(
                operationalStatus
              )}
            </strong>

            <span>
              Operational Status
            </span>
          </div>
        </article>

        <article>
          <div className="facility-dashboard-stat-icon">
            <Building2
              size={23}
            />
          </div>

          <div>
            <strong>
              {facility
                ?.facility_type ||
                "—"}
            </strong>

            <span>
              Facility Type
            </span>
          </div>
        </article>

        <article>
          <div className="facility-dashboard-stat-icon">
            <CalendarDays
              size={23}
            />
          </div>

          <div>
            <strong>
              {
                profileCompletion
              }
              %
            </strong>

            <span>
              Profile Complete
            </span>
          </div>
        </article>
      </section>

      {approvalStatus !==
        "approved" && (
        <section className="facility-dashboard-notice">
          <ShieldCheck
            size={23}
          />

          <div>
            <strong>
              Facility approval
              pending
            </strong>

            <p>
              Some staffing and
              assignment features
              may remain unavailable
              until Zeth Health
              completes your
              facility approval.
            </p>
          </div>
        </section>
      )}

      <section className="facility-dashboard-content-grid">
        <div className="facility-dashboard-panel">
          <div className="facility-dashboard-panel-header">
            <div>
              <p>
                FACILITY
                OPERATIONS
              </p>

              <h2>
                Portal Modules
              </h2>
            </div>
          </div>

          <div className="facility-dashboard-module-grid">
            {modules.map(
              (module) => {
                const Icon =
                  module.icon;

                const available =
                  module.status ===
                  "Available";

                return (
                  <button
                    type="button"
                    key={
                      module.title
                    }
                    className={`facility-dashboard-module ${
                      available
                        ? "available"
                        : "disabled"
                    }`}
                    onClick={
                      available
                        ? module.action
                        : undefined
                    }
                    disabled={
                      !available
                    }
                  >
                    <span className="facility-dashboard-module-icon">
                      <Icon
                        size={22}
                      />
                    </span>

                    <span className="facility-dashboard-module-copy">
                      <strong>
                        {
                          module.title
                        }
                      </strong>

                      <small>
                        {
                          module.description
                        }
                      </small>
                    </span>

                    <span
                      className={`facility-dashboard-module-status ${
                        available
                          ? "available"
                          : ""
                      }`}
                    >
                      {
                        module.status
                      }
                    </span>
                  </button>
                );
              }
            )}
          </div>
        </div>

        <aside className="facility-dashboard-panel">
          <div className="facility-dashboard-panel-header">
            <div>
              <p>
                ORGANIZATION
              </p>

              <h2>
                Facility
                Overview
              </h2>
            </div>
          </div>

          <div className="facility-dashboard-overview">
            <div>
              <span>
                Facility Name
              </span>

              <strong>
                {facility
                  ?.facility_name ||
                  "—"}
              </strong>
            </div>

            <div>
              <span>
                Legal Name
              </span>

              <strong>
                {facility
                  ?.legal_name ||
                  "Not provided"}
              </strong>
            </div>

            <div>
              <span>
                Contact Person
              </span>

              <strong>
                {facility
                  ?.contact_person ||
                  "Not provided"}
              </strong>
            </div>

            <div>
              <span>
                Email
              </span>

              <strong>
                {facility
                  ?.email ||
                  "Not provided"}
              </strong>
            </div>

            <div>
              <span>
                Phone
              </span>

              <strong>
                {facility
                  ?.phone ||
                  "Not provided"}
              </strong>
            </div>

            <div>
              <span>
                Location
              </span>

              <strong>
                {[
                  facility?.city,
                  facility
                    ?.province,
                ]
                  .filter(Boolean)
                  .join(", ") ||
                  "Not provided"}
              </strong>
            </div>
          </div>

          <div className="facility-dashboard-progress">
            <div>
              <span>
                Profile
                completion
              </span>

              <strong>
                {
                  profileCompletion
                }
                %
              </strong>
            </div>

            <div className="facility-dashboard-progress-track">
              <span
                style={{
                  width:
                    `${profileCompletion}%`,
                }}
              />
            </div>
          </div>
        </aside>
      </section>
    </main>
  );
}

export default FacilityDashboard;
